import type {
  EventHandlerCb,
  ExtendedConstraints,
  CapabilityConfig,
} from '../../types';
import { hasOnlyKeysFromSet } from '../../utils';

export const id = 'app-token';

export enum Events {
  GetStatus = 'app-token:get-status',
  /**
   * @deprecated will be removed soon, please use app-token:get-status event and its successful payload.
   */
  Update = 'app-token:update',
}

export enum Actions {
  Get = 'app-token:get',
}

/**
 * Config for the `app-token` capability.
 *
 * `features` declares which Frontegg feature entitlement keys this app wants
 * resolved into the JWT's `features` claim. Token generation queries Frontegg
 * for each declared key and embeds only the entitled subset into the token.
 *
 * Invariant (only relevant for apps with an api-extensions resolver gating on
 * token features): `features` must include every key the app's api-extensions
 * resolver checks for. Otherwise the JWT will lack the key and the resolver
 * will reject the request. Apps without an api-extensions backend can declare
 * any subset, including empty.
 *
 * Values are validated against `FronteggExtensionsFeatureKeys` server-side at
 * token-generation time. Unknown values are dropped silently.
 */
export type Config = CapabilityConfig & {
  type: typeof id;
  features?: string[];
};
/**
 * @deprecated UpdatePayload will be removed with update event.
 */
export type UpdatePayload = string;
export type GetStatusPayload = {
  isSuccess: boolean;
  errorMessage?: string;
  data?: string;
};

/**
 * @deprecated GetPayload will be removed from get event.
 */
export type GetPayload = {
  appName: string;
};

export type Constraints = ExtendedConstraints<{
  events: Events;
  actions: Actions;
  config: Config;
  eventPayloadTuple:
    | [Events.Update, UpdatePayload]
    | [Events.GetStatus, GetStatusPayload];
  eventHandlerTuple:
    | [Events.Update, EventHandlerCb<UpdatePayload>]
    | [Events.GetStatus, EventHandlerCb<GetStatusPayload>];
  actionPayloadTuple: [Actions.Get] | [Actions.Get, GetPayload];
}>;

const VALID_CONFIG_KEYS = ['type', 'features'] as const;
export function isValidConfig(config: CapabilityConfig): config is Config {
  if (!hasOnlyKeysFromSet(config, VALID_CONFIG_KEYS)) {
    return false;
  }

  if (config.type !== id) {
    return false;
  }

  const features = (config as { features?: unknown }).features;
  if (features !== undefined) {
    if (
      !Array.isArray(features) ||
      !features.every((f) => typeof f === 'string')
    ) {
      return false;
    }
  }

  return true;
}
